import type { WorkbookResult } from '@erp/shared-types';
import { lacksBaseRun } from '@erp/workbooks';
import type { WorkbookEditor } from './useWorkbookEditor';
import type { SheetPreview } from './SheetEditor';
import type { WorkbookRunResults } from './workbookRunResults';
import { useRunModeText, useRunModes } from './workbookRunModes';
import { useWorkbookPreview } from './useWorkbookPreview';

/**
 * What a Worksheet tab shows: a newer Run on sample's Result (marked as a
 * sample), else its Current Result, else a matching Live preview, else its
 * Stale Result.
 */
export type WorksheetResult = {
  result: WorkbookResult | undefined;
  /** Neither Current nor a matching Live preview. */
  stale: boolean;
  /** When the shown Result's Run was created; absent for a Live preview or no Result. */
  ranAt: string | undefined;
  /** The shown Result's Run; absent for a Live preview or no Result. */
  runId: string | undefined;
  preview: SheetPreview;
  /** What fills cells the shown Result has no values for. */
  needsRunHint: string;
};

/**
 * Keeps a Live preview of the Worksheet while `enabled` and it has no Current
 * Result but can run from itself — or, lacking only a Base run, take a first
 * look over each Source's first rows — and picks what its tab shows.
 */
export function useWorksheetResult(
  editor: Pick<WorkbookEditor, 'definition' | 'plan' | 'workbookId'> & {
    runs: Pick<WorkbookEditor['runs'], 'data'>;
  },
  runResults: WorkbookRunResults,
  sheetId: string | null,
  enabled: boolean,
): WorksheetResult {
  const { definition } = editor;
  const sheetResult = runResults.sheet(sheetId);
  const modes = useRunModes(editor, sheetId);
  const { needsRunHint } = useRunModeText(definition);
  const preview = useWorkbookPreview({
    workbookId: editor.workbookId,
    definition,
    sheetId,
    baseRunId: modes.from.baseRunId,
    enabled:
      enabled &&
      !sheetResult.current &&
      !sheetResult.sample &&
      (modes.from.allowed ||
        (lacksBaseRun(modes.from.blocker) && modes['up-to'].allowed)),
  });
  // A Live preview is shown only while it matches the edited Definition.
  const fingerprint = sheetId
    ? runResults.fingerprints.get(sheetId)
    : undefined;
  const { sample } = sheetResult;
  const previewResult =
    sheetResult.current || sample
      ? undefined
      : preview.results.find(
          (result) =>
            result.sheetId === sheetId && result.fingerprint === fingerprint,
        );
  if (sample)
    return {
      result: sample.result,
      stale: false,
      ranAt: sample.run.createdAt,
      runId: sample.run.id,
      preview: {
        shown: false,
        inputsAsOf: null,
        freshSources: [],
        reusedSourceCount: 0,
        headRows: null,
        pending: false,
        error: null,
        sample: { keys: sample.run.sampling?.report?.keys ?? null },
      },
      needsRunHint: needsRunHint(modes),
    };
  return {
    result: previewResult ?? sheetResult.result,
    stale: !sheetResult.current && !previewResult,
    ranAt: previewResult ? undefined : sheetResult.run?.createdAt,
    runId: previewResult ? undefined : sheetResult.run?.id,
    preview: {
      shown: !!previewResult,
      inputsAsOf: preview.inputsAsOf,
      freshSources: preview.freshSources,
      reusedSourceCount: preview.reusedSourceCount,
      headRows: preview.headRows,
      pending: preview.pending,
      error: preview.error,
    },
    needsRunHint: needsRunHint(modes),
  };
}
